import { Avatar, VideoModal, toast } from '@clareefai/ui';
import {
  ArrowLeft,
  Award,
  Briefcase,
  Calendar,
  Camera,
  CheckCircle,
  Clock,
  CreditCard,
  Crown,
  Download,
  Edit,
  ExternalLink,
  Eye,
  FileText,
  Gift,
  Linkedin,
  Mail,
  MapPin,
  Pencil,
  Phone,
  Play,
  Send,
  Share2,
  Sparkles,
  Star,
  Target,
  Trash2,
  TrendingUp,
  Trophy,
  Users,
  Video,
  Wand2,
  MessageSquare,
  RefreshCw,
  X,
  Loader2,
  UploadCloud
} from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageTitle from '../../components/shared/PageTitle';
import { Meeting } from '@clareefai/types';
import MeetingDetailsModal from '../../components/meetings/MeetingDetailsModal';
import EditPromoterModal from '../../components/promoters/EditPromoterModal';
import UploadPromoterVideoModal from '../../components/testimonials/UploadPromoterVideoModal';
import ConfirmDialog from '../../components/shared/ConfirmDialog';
import { FirstLoginChip, InvitedByLine } from '../../components/shared/ProfileMeta';
import promoterDetailData from '../../data/promoter-detail.json';
import type { PromoterTestimonialResponse } from '../../store/promoterApi';
import { useGetPromoterByIdQuery, useSendActionReminderMutation, useDeletePromoterMutation, useResendPromoterInvitationMutation, useAssignPromoterToTeamMemberMutation, useUploadPromoterCompanyLogoMutation } from '../../store/promoterApi';
import { useGetTeamMembersQuery } from '../../store/teamMemberApi';
import { useGetPromoterRewardsQuery } from '../../store/rewardApi';
import { useGetRewardSettingsQuery, usePayPendingRewardMutation } from '../../store/vendorApi';
import { useAbility } from '../../contexts/AbilityContext';
import { getBadgesForPromoter } from '../../utils/promoterLevel';

type TabType = 'overview' | 'progress' | 'reviews' | 'testimonials' | 'usecases' | 'meetings' | 'rewards';

const PromoterDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [showReviewResponse, setShowReviewResponse] = useState<string | null>(null);
  const [showVideoResponse, setShowVideoResponse] = useState<string | null>(null);
  const [reviewResponseText, setReviewResponseText] = useState('');
  const [videoResponseText, setVideoResponseText] = useState('');
  const [showShareMenu, setShowShareMenu] = useState<string | null>(null);
  const [editingResponse, setEditingResponse] = useState<string | null>(null);
  const [editResponseText, setEditResponseText] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [playingVideoUrl, setPlayingVideoUrl] = useState<string | null>(null);
  const [playingVideoTitle, setPlayingVideoTitle] = useState<string | undefined>(undefined);
  const [playingVideoDescription, setPlayingVideoDescription] = useState<string | undefined>(undefined);
  const [playingVideoTags, setPlayingVideoTags] = useState<string[]>([]);
  const [selectedMeetingForModal, setSelectedMeetingForModal] = useState<Meeting | null>(null);
  const [isMeetingModalOpen, setIsMeetingModalOpen] = useState(false);
  const [detailModalState, setDetailModalState] = useState<{ type: 'review' | 'video' | 'usecase'; data: any } | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showUploadVideoModal, setShowUploadVideoModal] = useState(false);

  const ability = useAbility();
  const canEditPromoter = ability.can('update', 'Promoter');
  const canDeletePromoter = ability.can('delete', 'Promoter');

  const { data: promoterResponse, isLoading, error, refetch } = useGetPromoterByIdQuery(id || '', { skip: !id });
  const { data: rewardsData } = useGetPromoterRewardsQuery({ promoterId: id || '' }, { skip: !id });
  const { data: rewardSettings } = useGetRewardSettingsQuery();
  const [sendActionReminder] = useSendActionReminderMutation();
  const [payPendingReward, { isLoading: isPayingReward }] = usePayPendingRewardMutation();
  const [deletePromoter, { isLoading: isDeletingPromoter }] = useDeletePromoterMutation();
  const [resendInvitation, { isLoading: isResending }] = useResendPromoterInvitationMutation();
  const [assignPromoterToTeamMember, { isLoading: isAssigning }] = useAssignPromoterToTeamMemberMutation();
  const [uploadCompanyLogo, { isLoading: isUploadingCompanyLogo }] = useUploadPromoterCompanyLogoMutation();
  const companyLogoInputRef = React.useRef<HTMLInputElement | null>(null);

  const handleCompanyLogoSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !id) return;
    if (!file.type.startsWith('image/')) {
      toast.error(t('promoterDetail.companyLogo.invalidType'));
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error(t('promoterDetail.companyLogo.tooLarge'));
      return;
    }
    try {
      await uploadCompanyLogo({ id, file }).unwrap();
      toast.success(t('promoterDetail.companyLogo.uploadSuccess'));
      refetch();
    } catch {
      toast.error(t('promoterDetail.companyLogo.uploadFailed'));
    }
  };
  const { data: teamMembersData = [] } = useGetTeamMembersQuery();

  const rewardAmountByType = useMemo(() => {
    const configs = rewardSettings?.reward_configs || [];
    const map: Record<string, number> = {};
    configs.forEach((c: { type: string; amount: number }) => {
      map[c.type.toUpperCase()] = c.amount;
    });
    return map;
  }, [rewardSettings]);

  // Map backend data to page structure
  const pageData = useMemo(() => {
    if (!promoterResponse) {
      return {
        ...(promoterDetailData as any),
        rescheduledMeetings: [],
        badges: getBadgesForPromoter(null),
      };
    }

    const progress = promoterResponse?.progress;
    const testimonials = promoterResponse.testimonials || [];
    const isPublished = (s: string) => s === 'APPROVED' || s === 'PUBLISHED' || s === 'FEATURED';

    // Use case title for display - handles JSON content with generated_use_case
    const useCaseTitle = (testimonial: PromoterTestimonialResponse) => {
      const raw = testimonial.content?.trim() || '';
      if (!raw) return t('promoterDetail.useCaseFallback');
      try {
        const parsed = JSON.parse(raw);
        const generated = parsed?.generated_use_case;
        if (typeof generated === 'string') {
          const firstLine = generated.split('\n')[0].replace(/^#+\s*/, '').trim();
          return firstLine.length > 80 ? firstLine.slice(0, 80) + '...' : firstLine || t('promoterDetail.useCaseFallback');
        }
      } catch {
        // Not JSON, use raw content
      }
      const firstLine = raw.split('\n')[0].trim();
      return firstLine.length > 80 ? firstLine.slice(0, 80) + '...' : firstLine;
    };

    // Use case description - short summary from generated_use_case (Company Overview or first paragraph, max ~150 chars)
    const useCaseDescription = (testimonial: PromoterTestimonialResponse) => {
      const raw = testimonial.content?.trim() || '';
      if (!raw) return '';
      try {
        const parsed = JSON.parse(raw);
        const generated = parsed?.generated_use_case;
        if (typeof generated === 'string') {
          const lines = generated.split('\n').map((l: string) => l.trim()).filter(Boolean);
          const overviewIdx = lines.findIndex((l: string) => l.startsWith('## Company Overview'));
          const descLine = overviewIdx >= 0 && lines[overviewIdx + 1]
            ? lines[overviewIdx + 1]
            : lines.find((l: string) => !l.startsWith('#'));
          const desc = (descLine || '').replace(/^#+\s*/, '').trim();
          return desc.length > 150 ? desc.slice(0, 150) + '...' : desc;
        }
      } catch {
        // Not JSON
      }
      const firstLine = raw.split('\n')[0].trim();
      return firstLine.length > 150 ? firstLine.slice(0, 150) + '...' : firstLine;
    };

    // Map reviews (all testimonials with type REVIEW - count all regardless of status)
    const reviews = testimonials
      .filter((ts: PromoterTestimonialResponse) => ts.type === 'REVIEW')
      .map((ts: PromoterTestimonialResponse) => ({
        id: ts.id,
        rating: ts.rating || 5,
        title: ts.content?.substring(0, 50) + (ts.content && ts.content.length > 50 ? '...' : '') || t('promoterDetail.review'),
        comment: ts.content || '',
        date: ts.createdAt,
        product: 'Product', // Not available in backend
        verified: isPublished(ts.status || ''),
        helpful: 0, // Not available in backend
        categories: [], // Not available in backend
        publicUrl: '', // Not available in backend
        status: ts.status,
        rawTestimonial: ts,
        vendorResponse: (ts as any).vendorResponse ? {
          author: t('promoterDetail.vendor'),
          date: ts.updatedAt,
          message: (ts as any).vendorResponse
        } : null
      }));

    // Map video testimonials (all - count all regardless of status)
    const videoTestimonials = testimonials
      .filter((ts: PromoterTestimonialResponse) => ts.type === 'VIDEO')
      .map((ts: PromoterTestimonialResponse) => ({
        id: ts.id,
        title: ts.videoTitle || t('promoterDetail.videoTestimonial'),
        description: ts.videoDescription || '',
        thumbnailUrl: ts.videoThumbnailUrl || '',
        videoUrl: ts.videoUrl || '',
        duration: ts.videoDuration ? `${Math.floor(ts.videoDuration / 60)}:${String(ts.videoDuration % 60).padStart(2, '0')}` : '0:00',
        views: 0, // Not available in backend
        date: ts.createdAt,
        status: ts.status === 'APPROVED' || ts.status === 'PUBLISHED' || ts.status === 'FEATURED' ? 'published' : 'pending',
        tags: ts.videoTags || [],
        transcript: '', // Not available in backend
        likes: 0, // Not available in backend
        rawTestimonial: ts,
        vendorResponse: (ts as any).vendorResponse ? {
          author: t('promoterDetail.vendor'),
          date: ts.updatedAt,
          message: (ts as any).vendorResponse
        } : null
      }));

    // Map use cases (all - count all regardless of status)
    const useCases = testimonials
      .filter((ts: PromoterTestimonialResponse) => ts.type === 'USE_CASE')
      .map((ts: PromoterTestimonialResponse) => ({
        id: ts.id,
        title: useCaseTitle(ts),
        description: useCaseDescription(ts),
        fullDescription: ts.content || '',
        tags: [], // Not available in backend
        status: isPublished(ts.status || '') ? 'published' : 'pending',
        views: 0, // Not available in backend
        downloads: 0, // Not available in backend
        publishedDate: ts.createdAt,
        industry: '', // Not available in backend
        companySize: promoterResponse.companySize || '',
        challenges: [], // Not available in backend
        solutions: [], // Not available in backend
        results: [], // Not available in backend
        pdfUrl: '', // Not available in backend
        featured: ts.isFeatured,
        rawTestimonial: ts
      }));

    const meetings = promoterResponse.meetings || [];

    const meetingsRequested = meetings
      .filter((meeting) => meeting.status === 'requested' || meeting.status === 'request')
      .map((meeting) => ({
        id: meeting.id,
        prospectName: meeting.prospect_name || '',
        prospectPosition: '',
        prospectCompany: meeting.prospect_company || '',
        topic: meeting.type || 'Meeting',
        notes: meeting.notes || '',
        preferredDates: (meeting.requested_dates || []).map((date) => date.date)
      }));

    const upcomingMeetings = meetings
      .filter((meeting) => meeting.status === 'upcoming' || meeting.status === 'booked')
      .map((meeting) => ({
        id: meeting.id,
        prospectName: meeting.prospect_name || '',
        prospectPosition: '',
        prospectCompany: meeting.prospect_company || '',
        prospectAvatar: meeting.prospect_avatar,
        scheduledDate: meeting.accepted_date?.date || meeting.date || meeting.requested_dates?.[0]?.date || '',
        scheduledTime: meeting.accepted_date?.time || meeting.time || meeting.requested_dates?.[0]?.time || '',
        duration: 30,
        topic: meeting.type || 'Meeting'
      }));

    const rescheduledMeetings = meetings
      .filter((meeting) => meeting.status === 'rescheduled')
      .map((meeting) => ({
        id: meeting.id,
        prospectName: meeting.prospect_name || '',
        prospectCompany: meeting.prospect_company || '',
        rescheduledDates: (meeting.rescheduled_dates || []).map((date) => date.date),
        notes: meeting.notes || ''
      }));

    const meetingHistory = meetings
      .filter((meeting) => meeting.status === 'past' || meeting.status === 'completed' || meeting.status === 'partially_attended')
      .map((meeting) => {
        const durationMinutes = meeting.duration_minutes ?? (
          meeting.meeting_started_at && meeting.meeting_ended_at
            ? Math.round((new Date(meeting.meeting_ended_at).getTime() - new Date(meeting.meeting_started_at).getTime()) / 60000)
            : null
        );

        return {
          id: meeting.id,
          prospectName: meeting.prospect_name || '',
          prospectPosition: '',
          prospectCompany: meeting.prospect_company || '',
          meetingDate: meeting.accepted_date?.date || meeting.date || meeting.requested_dates?.[0]?.date || '',
          meetingTime: meeting.accepted_date?.time || meeting.time || meeting.requested_dates?.[0]?.time || '',
          duration: durationMinutes,
          outcome: meeting.outcome || '',
          rewardEarned: 0,
          rating: meeting.rating || 0,
          feedback: null,
          notes: meeting.notes || '',
          prospectJoinedAt: meeting.prospect_joined_at || null,
          promoterJoinedAt: meeting.promoter_joined_at || null,
          meetingStartedAt: meeting.meeting_started_at || null,
          meetingEndedAt: meeting.meeting_ended_at || null
        };
      });

    // Map progress actions (reward amounts from vendor's reward settings)
    const progressActions: Array<{
      id: string;
      title: string;
      description: string;
      reward: number;
      currency: string;
      completed: boolean;
      completedDate?: string;
      icon: string;
      category: string;
      perCall?: boolean;
      perReferral?: boolean;
      totalCalls?: number;
      totalReferrals?: number;
    }> = [
        {
          id: '1',
          title: t('promoterDetail.progress.actions.reviewTitle'),
          description: t('promoterDetail.progress.actions.reviewDesc'),
          reward: rewardAmountByType.REVIEW ?? 10,
          currency: rewardSettings?.currency ?? 'EUR',
          completed: progress?.reviewCompleted || reviews.length > 0,
          completedDate: progress?.reviewCompleted || reviews.length > 0 ? (reviews.length > 0 ? reviews[0].date : promoterResponse.createdAt || undefined) : undefined,
          icon: 'star',
          category: 'engagement'
        },
        {
          id: '2',
          title: t('promoterDetail.progress.actions.videoTitle'),
          description: t('promoterDetail.progress.actions.videoDesc'),
          reward: rewardAmountByType.VIDEO ?? 10,
          currency: rewardSettings?.currency ?? 'EUR',
          completed: progress?.testimonialCompleted || videoTestimonials.length > 0,
          completedDate: (progress?.testimonialCompleted || videoTestimonials.length > 0) ? (videoTestimonials.length > 0 ? videoTestimonials[0].date : promoterResponse.updatedAt || undefined) : undefined,
          icon: 'video',
          category: 'content'
        },
        {
          id: '3',
          title: t('promoterDetail.progress.actions.useCaseTitle'),
          description: t('promoterDetail.progress.actions.useCaseDesc'),
          reward: rewardAmountByType.USE_CASE ?? 10,
          currency: rewardSettings?.currency ?? 'EUR',
          completed: progress?.useCaseCompleted || useCases.length > 0,
          completedDate: (progress?.useCaseCompleted || useCases.length > 0) ? (useCases.length > 0 ? useCases[0].publishedDate : promoterResponse.updatedAt || undefined) : undefined,
          icon: 'file-text',
          category: 'content'
        },
        {
          id: '4',
          title: t('promoterDetail.progress.actions.callTitle'),
          description: t('promoterDetail.progress.actions.callDesc'),
          reward: rewardAmountByType.REFERENCE_CALL ?? 100,
          currency: rewardSettings?.currency ?? 'EUR',
          perCall: true,
          completed: progress?.firstMeetingCompleted || meetingHistory.length > 0,
          completedDate: (progress?.firstMeetingCompleted || meetingHistory.length > 0) ? (meetingHistory.length > 0 ? meetingHistory[0].meetingDate : promoterResponse.updatedAt || undefined) : undefined,
          icon: 'phone',
          category: 'advocacy'
        }
      ];

    return {
      promoter: {
        id: promoterResponse.id || '',
        name: `${promoterResponse.firstName} ${promoterResponse.lastName}`,
        email: promoterResponse.email,
        phone: promoterResponse.phone || '',
        company: promoterResponse.company || '',
        position: '', // Not available in backend
        avatar: promoterResponse.avatar || '',
        companyLogo: promoterResponse.companyLogo || '',
        status: promoterResponse.status || 'invited',
        prospectsReferred: promoterResponse.prospectsReferred || 0,
        conversionRate: promoterResponse.conversionRate || 0,
        totalRewards: promoterResponse.totalRewards || 0,
        joinedDate: promoterResponse.joinedDate || promoterResponse.createdAt || '',
        lastActivity: promoterResponse.updatedAt || '',
        sectors: promoterResponse.industrySectors || [],
        companySize: promoterResponse.companySize || '',
        location: promoterResponse.customFields?.address || promoterResponse.metadata?.address || '',
        linkedin: promoterResponse.linkedin || '',
        bio: '', // Not available in backend
        expertise: [], // Not available in backend
        languages: [] // Not available in backend
      },
      statistics: {
        totalReferrals: promoterResponse.prospectsReferred || 0,
        successfulConversions: Math.round((promoterResponse.prospectsReferred || 0) * (promoterResponse.conversionRate || 0) / 100),
        conversionRate: promoterResponse.conversionRate || 0,
        averageResponseTime: '2 hours', // Not available in backend
        totalRewardsEarned: rewardsData?.content && rewardsData.content.length > 0
          ? rewardsData.content.reduce((sum: number, reward: any) => sum + (reward.amount || 0), 0)
          : (promoterResponse.totalRewards || 0),
        totalMeetingsConducted: meetingHistory.length,
        averageRating: reviews.length > 0 ? reviews.reduce((sum: number, r: any) => sum + (r.rating || 0), 0) / reviews.length : 0,
        activeProspects: upcomingMeetings.length + meetingsRequested.length,
        totalReviews: reviews.length,
        totalVideos: videoTestimonials.length,
        totalUseCases: useCases.length
      },
      progressActions,
      reviews,
      videoTestimonials,
      useCases,
      meetingsRequested,
      upcomingMeetings,
      rescheduledMeetings,
      meetingHistory,
      rewardHistory: rewardsData?.content || [],
      badges: getBadgesForPromoter(promoterResponse),
    };
  }, [promoterResponse, rewardsData, rewardAmountByType, rewardSettings, t]);

  const {
    promoter,
    statistics,
    progressActions,
    reviews,
    videoTestimonials,
    useCases,
    meetingsRequested,
    upcomingMeetings,
    rescheduledMeetings,
    meetingHistory,
    rewardHistory,
    badges
  } = pageData;

  const tabs = [
    { id: 'overview' as TabType, label: t('promoterDetail.tabs.overview'), icon: Target },
    { id: 'progress' as TabType, label: t('promoterDetail.tabs.progress'), icon: TrendingUp },
    { id: 'reviews' as TabType, label: t('promoterDetail.tabs.reviews'), icon: Star },
    { id: 'testimonials' as TabType, label: t('promoterDetail.tabs.testimonials'), icon: Video },
    { id: 'usecases' as TabType, label: t('promoterDetail.tabs.usecases'), icon: FileText },
    { id: 'meetings' as TabType, label: t('promoterDetail.tabs.meetings'), icon: Calendar },
    { id: 'rewards' as TabType, label: t('promoterDetail.tabs.rewards'), icon: Gift }
  ];

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const mapPromoterMeetingToMeeting = (raw: any): Meeting => {
    const statusMap: Record<string, Meeting['status']> = {
      past: 'completed',
      completed: 'completed',
      partially_attended: 'completed',
      requested: 'request',
      request: 'request',
      upcoming: 'booked',
      booked: 'booked',
      cancelled: 'rebook',
      rescheduled: 'rebook',
      progress: 'progress',
    };
    return {
      ...raw,
      status: statusMap[raw?.status] || 'rebook',
      requested_dates: raw?.requested_dates || [],
      rescheduled_dates: raw?.rescheduled_dates || [],
      accepted_date: raw?.accepted_date || null,
    };
  };

  const openMeetingModal = (meetingId: string) => {
    const raw = promoterResponse?.meetings?.find((m: any) => m.id === meetingId);
    if (raw) {
      setSelectedMeetingForModal(mapPromoterMeetingToMeeting(raw));
      setIsMeetingModalOpen(true);
    }
  };

  const openDetailModalByTestimonialId = (testimonialId: string, rewardType: string) => {
    if (rewardType === 'USE_CASE') {
      const usecase = useCases.find((u: any) => u.id === testimonialId);
      if (usecase?.rawTestimonial && id) {
        navigate('/use-case-submission/' + testimonialId, { state: { testimonial: { ...usecase.rawTestimonial, promoterId: id, promoterName: promoter.name, promoterCompany: promoter.company } } });
        return;
      }
      navigate('/use-case-submission/' + testimonialId, { state: { testimonial: { id: testimonialId, promoterId: id, promoterName: promoter.name, promoterCompany: promoter.company } } });
      return;
    }
    const review = reviews.find((r: any) => r.id === testimonialId);
    if (review && rewardType === 'REVIEW') {
      setDetailModalState({ type: 'review', data: review });
      return;
    }
    const video = videoTestimonials.find((v: any) => v.id === testimonialId);
    if (video && rewardType === 'VIDEO') {
      setDetailModalState({ type: 'video', data: video });
      return;
    }
    // Fallback: navigate to detail page
    if (rewardType === 'REVIEW') navigate('/reviews/' + testimonialId, { state: { review: { id: testimonialId, promoterId: id } } });
    else if (rewardType === 'VIDEO') navigate('/testimonials/' + testimonialId, { state: { testimonial: { id: testimonialId, promoterId: id } } });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 flex items-center justify-center">
        <div className="text-gray-500">{t('promoterDetail.loading')}</div>
      </div>
    );
  }

  if (error || !promoterResponse) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 flex items-center justify-center">
        <div className="text-red-500">{t('promoterDetail.loadError')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageTitle title={t('promoterDetail.pageTitle', { name: promoter.name })} />
      <button
        onClick={() => navigate('/promoters')}
        className="group -ml-1 flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors"
      >
        <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" />
        <span>{t('promoterDetail.backToPromoters')}</span>
      </button>

      {/* Hero — promoter identity + stats */}
      <div className="feel-in relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#4339A0] via-[#5a4dba] to-[#3d2f8f] text-white shadow-lg shadow-[#4339A0]/20">
        <div className="p-6 sm:p-8">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
            <div className="flex items-start gap-5">
              <div className="relative shrink-0">
                <Avatar
                  src={promoter.avatar || promoter.companyLogo || undefined}
                  name={promoter.name}
                  size="lg"
                  className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl ring-4 ring-white/30 shadow-lg"
                />
                <div className="absolute -bottom-1.5 -right-1.5 bg-yellow-400 text-yellow-900 p-1.5 rounded-lg shadow-md">
                  <Crown className="w-5 h-5" />
                </div>
                {canEditPromoter && (
                  <>
                    <input
                      ref={companyLogoInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleCompanyLogoSelected}
                    />
                    <button
                      type="button"
                      onClick={() => companyLogoInputRef.current?.click()}
                      disabled={isUploadingCompanyLogo}
                      title={t('promoterDetail.companyLogo.uploadButton')}
                      className="absolute top-1 right-1 bg-white/90 hover:bg-white active:scale-[0.92] text-gray-700 p-1.5 rounded-full shadow-md transition-[background-color,transform] duration-150 disabled:opacity-50"
                    >
                      {isUploadingCompanyLogo ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Camera className="w-4 h-4" />
                      )}
                    </button>
                  </>
                )}
              </div>

              <div className="min-w-0">
                <h1 className="text-2xl sm:text-3xl font-bold text-balance">{promoter.name}</h1>
                {promoter.position && <p className="text-base text-[#cfc8f5] mt-1">{promoter.position}</p>}
                {promoter.company && <p className="text-sm text-[#b8acec]">{promoter.company}</p>}

                <div className="flex flex-wrap items-center gap-2 mt-3">
                  <FirstLoginChip loggedIn={promoterResponse?.hasLoggedIn} />
                  {promoter.location && (
                    <span className="inline-flex items-center gap-1.5 bg-[#4339A0]/40 backdrop-blur-sm px-3 py-1 rounded-full text-xs">
                      <MapPin className="w-3.5 h-3.5" />
                      <span>{promoter.location}</span>
                    </span>
                  )}
                  {promoter.companySize && (
                    <span className="inline-flex items-center gap-1.5 bg-[#4339A0]/40 backdrop-blur-sm px-3 py-1 rounded-full text-xs">
                      <Briefcase className="w-3.5 h-3.5" />
                      <span>{promoter.companySize}</span>
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-1 mt-4">
                  <a
                    href={`mailto:${promoter.email}`}
                    aria-label="Email"
                    className="flex items-center justify-center w-9 h-9 rounded-lg text-[#cfc8f5] hover:text-white hover:bg-white/10 active:scale-[0.92] transition-[color,background-color,transform] duration-150"
                  >
                    <Mail className="w-5 h-5" />
                  </a>
                  {promoter.linkedin && (
                    <a
                      href={promoter.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="LinkedIn"
                      className="flex items-center justify-center w-9 h-9 rounded-lg text-[#cfc8f5] hover:text-white hover:bg-white/10 active:scale-[0.92] transition-[color,background-color,transform] duration-150"
                    >
                      <Linkedin className="w-5 h-5" />
                    </a>
                  )}
                </div>

                {(canEditPromoter || canDeletePromoter) && (
                  <div className="flex flex-wrap items-center gap-2 mt-4">
                    {canEditPromoter && (
                      <button
                        onClick={() => setShowEditModal(true)}
                        className="inline-flex items-center justify-center gap-1.5 h-9 pl-3 pr-3.5 bg-white/15 hover:bg-white/25 active:scale-[0.96] text-white text-xs font-medium rounded-lg transition-[background-color,transform] duration-150"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                        <span>{t('promoterDetail.actions.edit')}</span>
                      </button>
                    )}
                    {canEditPromoter && (
                      <button
                        onClick={async () => {
                          if (!id) return;
                          try {
                            await resendInvitation(id).unwrap();
                            toast.success(t('promoterDetail.actions.magicLinkSuccess'));
                          } catch {
                            toast.error(t('promoterDetail.actions.magicLinkFailed'));
                          }
                        }}
                        disabled={isResending}
                        className="inline-flex items-center justify-center gap-1.5 h-9 pl-3 pr-3.5 bg-white/15 hover:bg-white/25 active:scale-[0.96] disabled:opacity-60 disabled:active:scale-100 text-white text-xs font-medium rounded-lg transition-[background-color,transform] duration-150"
                      >
                        <RefreshCw className={`w-3.5 h-3.5 ${isResending ? 'animate-spin' : ''}`} />
                        <span>{isResending ? t('promoterDetail.actions.sending') : t('promoterDetail.actions.sendMagicLink')}</span>
                      </button>
                    )}
                    {canDeletePromoter && (
                      <button
                        onClick={() => setShowDeleteDialog(true)}
                        className="inline-flex items-center justify-center gap-1.5 h-9 pl-3 pr-3.5 bg-red-500/80 hover:bg-red-600 active:scale-[0.96] text-white text-xs font-medium rounded-lg transition-[background-color,transform] duration-150"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        <span>{t('promoterDetail.actions.delete')}</span>
                      </button>
                    )}
                    {/* DEV-66 — entry point to request/generate a use case for this promoter */}
                    <button
                      onClick={() => navigate('/request-use-case', { state: { promoterId: id, promoterName: promoter.name } })}
                      className="inline-flex items-center justify-center gap-1.5 h-9 pl-3 pr-3.5 bg-white text-[#4339A0] text-xs rounded-lg font-medium hover:bg-gray-50 active:scale-[0.96] transition-[background-color,transform] duration-150"
                    >
                      <Sparkles className="w-3.5 h-3.5" />
                      <span>{t('promoterDetail.actions.requestUseCase')}</span>
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 lg:w-[360px] shrink-0">
              <div className="rounded-xl bg-white/10 backdrop-blur-sm p-4 ring-1 ring-inset ring-white/15">
                <div className="flex items-center justify-between mb-2">
                  <Gift className="w-6 h-6 text-yellow-300" />
                  <Sparkles className="w-4 h-4 text-yellow-300" />
                </div>
                <div className="text-2xl sm:text-3xl font-bold tabular-nums">{formatCurrency(statistics.totalRewardsEarned)}</div>
                <div className="text-xs text-[#b8acec] mt-1 leading-tight">{t('promoterDetail.stats.totalRewards')}</div>
              </div>

              <div className="rounded-xl bg-white/10 backdrop-blur-sm p-4 ring-1 ring-inset ring-white/15">
                <Users className="w-6 h-6 text-[#b8acec] mb-2" />
                <div className="text-2xl sm:text-3xl font-bold tabular-nums">{statistics.totalReferrals}</div>
                <div className="text-xs text-[#b8acec] mt-1 leading-tight">{t('promoterDetail.stats.totalReferrals')}</div>
              </div>

              <div className="rounded-xl bg-white/10 backdrop-blur-sm p-4 ring-1 ring-inset ring-white/15">
                <TrendingUp className="w-6 h-6 text-[#b8acec] mb-2" />
                <div className="text-2xl sm:text-3xl font-bold tabular-nums">{statistics.conversionRate}%</div>
                <div className="text-xs text-[#b8acec] mt-1 leading-tight">{t('promoterDetail.stats.conversionRate')}</div>
              </div>

              <div className="rounded-xl bg-white/10 backdrop-blur-sm p-4 ring-1 ring-inset ring-white/15">
                <Star className="w-6 h-6 text-yellow-300 mb-2" />
                <div className="text-2xl sm:text-3xl font-bold tabular-nums">{statistics.averageRating}</div>
                <div className="text-xs text-[#b8acec] mt-1 leading-tight">{t('promoterDetail.stats.averageRating')}</div>
              </div>
            </div>
          </div>

          {badges.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-2">
              {badges.map((badge) => {
                const rarityColors: Record<string, string> = {
                  legendary: 'bg-gradient-to-r from-yellow-400 to-orange-500 text-white',
                  epic: 'bg-gradient-to-r from-purple-500 to-pink-500 text-white',
                  rare: 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white',
                };
                return (
                  <div
                    key={badge.id}
                    className={`flex items-center gap-2 px-3.5 py-1.5 rounded-full ${rarityColors[badge.rarity] ?? 'bg-gray-200 text-gray-800'} text-sm font-medium shadow-md`}
                  >
                    <Trophy className="w-4 h-4" />
                    <span>{badge.name}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="space-y-6">
        <div className="feel-in bg-white rounded-2xl shadow-sm ring-1 ring-gray-200/70 overflow-hidden sticky top-0 z-10" style={{ animationDelay: '80ms' }}>
          <div className="flex overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;

              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-6 py-4 font-medium transition-[color,background-color,border-color] duration-150 whitespace-nowrap ${isActive
                      ? 'text-[#4339A0] border-b-2 border-[#4339A0] bg-[#f5f3ff]'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('promoterDetail.overview.about', { name: promoter.name.split(' ')[0] })}</h2>
              {promoter.bio && (
                <p className="text-gray-600 text-lg leading-relaxed mb-6">{promoter.bio}</p>
              )}

              {promoter.expertise && promoter.expertise.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-900 mb-3">{t('promoterDetail.overview.expertise')}</h3>
                  <div className="flex flex-wrap gap-2">
                    {promoter.expertise.map((skill: string, idx: number) => (
                      <span key={idx} className="bg-[#f5f3ff] text-[#4339A0] px-3 py-1 rounded-full text-sm font-medium">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-gradient-to-br from-[#f5f3ff] to-[#ebe5ff] rounded-xl p-4">
                  <Calendar className="w-6 h-6 text-[#4339A0] mb-2" />
                  <div className="text-sm text-[#4339A0] font-medium">{t('promoterDetail.overview.joined')}</div>
                  <div className="text-gray-900 font-semibold">{formatDate(promoter.joinedDate)}</div>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4">
                  <Phone className="w-6 h-6 text-purple-600 mb-2" />
                  <div className="text-sm text-purple-700 font-medium">{t('promoterDetail.overview.meetingsConducted')}</div>
                  <div className="text-gray-900 font-semibold">{statistics.totalMeetingsConducted}</div>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4">
                  <Star className="w-6 h-6 text-blue-600 mb-2" />
                  <div className="text-sm text-blue-700 font-medium">{t('promoterDetail.overview.totalReviews')}</div>
                  <div className="text-gray-900 font-semibold">{statistics.totalReviews}</div>
                </div>

                <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-4">
                  <Video className="w-6 h-6 text-red-600 mb-2" />
                  <div className="text-sm text-red-700 font-medium">{t('promoterDetail.overview.totalVideos')}</div>
                  <div className="text-gray-900 font-semibold">{statistics.totalVideos}</div>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4">
                  <Users className="w-6 h-6 text-green-600 mb-2" />
                  <div className="text-sm text-green-700 font-medium">{t('promoterDetail.overview.activeProspects')}</div>
                  <div className="text-gray-900 font-semibold">{statistics.activeProspects}</div>
                </div>

                <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-4">
                  <FileText className="w-6 h-6 text-amber-600 mb-2" />
                  <div className="text-sm text-amber-700 font-medium">{t('promoterDetail.overview.useCases')}</div>
                  <div className="text-gray-900 font-semibold">{statistics.totalUseCases}</div>
                </div>
              </div>

              {(promoter.email || promoter.phone || promoter.company || (promoter.sectors && promoter.sectors.length > 0) || promoter.linkedin || promoterResponse?.timezone || promoterResponse?.referenceCallFrequency) && (
                <div className="mt-8 pt-8 border-t border-gray-200">
                  <h3 className="font-semibold text-gray-900 mb-4">{t('promoterDetail.overview.contactInfo')}</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {promoter.email && (
                      <div className="flex items-center space-x-3">
                        <Mail className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        <a href={`mailto:${promoter.email}`} className="text-[#4339A0] hover:text-[#3d2f8f] font-medium">
                          {promoter.email}
                        </a>
                      </div>
                    )}
                    {promoter.phone && (
                      <div className="flex items-center space-x-3">
                        <Phone className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        <a href={`tel:${promoter.phone}`} className="text-gray-900 font-medium">
                          {promoter.phone}
                        </a>
                      </div>
                    )}
                    {promoter.company && (
                      <div className="flex items-center space-x-3">
                        <Briefcase className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        <span className="text-gray-900 font-medium">{promoter.company}</span>
                      </div>
                    )}
                    {promoter.companySize && (
                      <div className="flex items-center space-x-3">
                        <Users className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        <span className="text-gray-900 font-medium">{promoter.companySize}</span>
                      </div>
                    )}
                    {promoter.linkedin && (
                      <div className="flex items-center space-x-3">
                        <Linkedin className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        <a href={promoter.linkedin} target="_blank" rel="noopener noreferrer" className="text-[#4339A0] hover:text-[#3d2f8f] font-medium">
                          {t('promoterDetail.overview.linkedinProfile')}
                        </a>
                      </div>
                    )}
                    {promoterResponse?.timezone && (
                      <div className="flex items-center space-x-3">
                        <Clock className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        <span className="text-gray-900 font-medium">{promoterResponse.timezone}</span>
                      </div>
                    )}
                    {promoterResponse?.referenceCallFrequency && (
                      <div className="flex items-center space-x-3">
                        <Calendar className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        <span className="text-gray-900 font-medium">{promoterResponse.referenceCallFrequency?.replace(/_/g, ' ') || promoterResponse.referenceCallFrequency}</span>
                      </div>
                    )}
                  </div>
                  {promoter.sectors && promoter.sectors.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-gray-600 mb-2">{t('promoterDetail.overview.industrySectors')}</h4>
                      <div className="flex flex-wrap gap-2">
                        {promoter.sectors.map((sector: string, idx: number) => (
                          <span key={idx} className="bg-[#f5f3ff] text-[#4339A0] px-3 py-1 rounded-full text-sm font-medium">
                            {sector.replace(/_/g, ' ')}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {(promoterResponse?.assignedTeamMember || promoterResponse?.createdByTeamMember || promoterResponse?.invitedBy) && (
                <div className="mt-8 pt-8 border-t border-gray-200">
                  <h3 className="font-semibold text-gray-900 mb-4">{t('promoterDetail.overview.account', 'Account')}</h3>
                  <div className="space-y-3 text-gray-900">
                    {(() => {
                      const assigned = promoterResponse.assignedTeamMember;
                      const creator = promoterResponse.createdByTeamMember;
                      const isSame = assigned && creator && assigned.id === creator.id;
                      return (
                        <>
                          {!creator && promoterResponse.invitedBy && (
                            <InvitedByLine invitedBy={promoterResponse.invitedBy} />
                          )}
                          {isSame && assigned ? (
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                <Avatar src={assigned.avatar} name={`${assigned.firstName} ${assigned.lastName}`} size="sm" />
                                <div>
                                  <span className="text-sm font-medium text-gray-900">{assigned.firstName} {assigned.lastName}</span>
                                  <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">{assigned.role}</span>
                                  <p className="text-xs text-gray-500">{t('promoterDetail.overview.ownerInvitedBy', 'Owner (invited by this person)')}</p>
                                </div>
                              </div>
                              {canEditPromoter && (
                                <div className="flex items-center space-x-2">
                                  <select
                                    disabled={isAssigning}
                                    defaultValue={assigned.id}
                                    onChange={async (e) => {
                                      const val = e.target.value || null;
                                      if (!id) return;
                                      try {
                                        await assignPromoterToTeamMember({ promoterId: id, teamMemberId: val }).unwrap();
                                        toast.success(t('promoterDetail.overview.assignSuccess', 'Owner updated'));
                                      } catch {
                                        toast.error(t('promoterDetail.overview.assignFailed', 'Failed to update owner'));
                                      }
                                    }}
                                    className="text-sm border border-gray-300 rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50"
                                  >
                                    <option value="">{t('promoterDetail.overview.unassign', 'Unassign')}</option>
                                    {teamMembersData.map(m => (
                                      <option key={m.id} value={m.id}>{m.firstName} {m.lastName}</option>
                                    ))}
                                  </select>
                                  {isAssigning && <Loader2 className="w-4 h-4 animate-spin text-gray-500" />}
                                </div>
                              )}
                            </div>
                          ) : (
                            <>
                              {creator && (
                                <div className="flex items-center space-x-3">
                                  <Avatar src={creator.avatar} name={`${creator.firstName} ${creator.lastName}`} size="sm" />
                                  <div>
                                    <span className="text-sm text-gray-700">{t('promoterDetail.overview.invitedBy', 'Invited by')}: </span>
                                    <span className="text-sm font-medium text-gray-900">{creator.firstName} {creator.lastName}</span>
                                    <span className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">{creator.role}</span>
                                  </div>
                                </div>
                              )}
                              {assigned && (
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center space-x-3">
                                    <Avatar src={assigned.avatar} name={`${assigned.firstName} ${assigned.lastName}`} size="sm" />
                                    <div>
                                      <span className="text-sm text-gray-700">{t('promoterDetail.overview.owner', 'Owner')}: </span>
                                      <span className="text-sm font-medium text-gray-900">{assigned.firstName} {assigned.lastName}</span>
                                      <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">{assigned.role}</span>
                                    </div>
                                  </div>
                                  {canEditPromoter && (
                                    <div className="flex items-center space-x-2">
                                      <select
                                        disabled={isAssigning}
                                        defaultValue={assigned.id}
                                        onChange={async (e) => {
                                          const val = e.target.value || null;
                                          if (!id) return;
                                          try {
                                            await assignPromoterToTeamMember({ promoterId: id, teamMemberId: val }).unwrap();
                                            toast.success(t('promoterDetail.overview.assignSuccess', 'Owner updated'));
                                          } catch {
                                            toast.error(t('promoterDetail.overview.assignFailed', 'Failed to update owner'));
                                          }
                                        }}
                                        className="text-sm border border-gray-300 rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50"
                                      >
                                        <option value="">{t('promoterDetail.overview.unassign', 'Unassign')}</option>
                                        {teamMembersData.map(m => (
                                          <option key={m.id} value={m.id}>{m.firstName} {m.lastName}</option>
                                        ))}
                                      </select>
                                      {isAssigning && <Loader2 className="w-4 h-4 animate-spin text-gray-500" />}
                                    </div>
                                  )}
                                </div>
                              )}
                            </>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'progress' && (
          <div className="grid md:grid-cols-2 gap-6">
            {progressActions.map((action: {
              id: string;
              title: string;
              description: string;
              reward: number;
              currency: string;
              completed: boolean;
              completedDate?: string;
              icon: string;
              category: string;
              perCall?: boolean;
              perReferral?: boolean;
              totalCalls?: number;
              totalReferrals?: number;
            }) => {
              const icons = {
                star: Star,
                video: Video,
                'file-text': FileText,
                phone: Phone,
                users: Users
              };

              const Icon = icons[action.icon as keyof typeof icons] || Target;

              const gradients = {
                engagement: 'from-amber-400 to-orange-500',
                content: 'from-rose-400 to-pink-500',
                advocacy: 'from-cyan-400 to-blue-500',
                referral: 'from-emerald-400 to-teal-500'
              };

              const gradient = gradients[action.category as keyof typeof gradients];

              // Determine if reminder button should be shown
              let showReminderButton = false;
              let reminderAction: 'REVIEW' | 'USE_CASE' | 'FIRST_MEETING' | null = null;
              let reminderAlreadySent = false;

              if (action.id === '1') {
                // Review reminder: show if not completed and reminder not sent
                showReminderButton = !action.completed && !promoterResponse?.reviewReminderSentAt;
                reminderAction = 'REVIEW';
                reminderAlreadySent = !!promoterResponse?.reviewReminderSentAt;
              } else if (action.id === '2') {
                // Video: no reminder button (as per requirements)
                showReminderButton = false;
              } else if (action.id === '3') {
                // Use case reminder: show if review completed, use case not completed, and reminder not sent
                const reviewCompleted = progressActions[0]?.completed || false;
                showReminderButton = reviewCompleted && !action.completed && !promoterResponse?.useCaseReminderSentAt;
                reminderAction = 'USE_CASE';
                reminderAlreadySent = !!promoterResponse?.useCaseReminderSentAt;
              } else if (action.id === '4') {
                // First meeting reminder: show if review and use case completed, first meeting not completed, and reminder not sent
                const reviewCompleted = progressActions[0]?.completed || false;
                const useCaseCompleted = progressActions[2]?.completed || false;
                showReminderButton = reviewCompleted && useCaseCompleted && !action.completed && !promoterResponse?.firstMeetingReminderSentAt;
                reminderAction = 'FIRST_MEETING';
                reminderAlreadySent = !!promoterResponse?.firstMeetingReminderSentAt;
              }

              const handleSendReminder = async () => {
                if (!id || !reminderAction) return;
                try {
                  await sendActionReminder({ promoterId: id, action: reminderAction }).unwrap();
                  toast.success(t('promoterDetail.progress.reminderSuccess'));
                } catch (error: any) {
                  toast.error(error?.data?.message || error?.message || t('promoterDetail.progress.reminderFailed'));
                }
              };

              return (
                <div
                  key={action.id}
                  className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
                >
                  <div className={`h-2 bg-gradient-to-r ${gradient}`} />
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className={`w-14 h-14 bg-gradient-to-br ${gradient} rounded-xl flex items-center justify-center`}>
                        <Icon className="w-7 h-7 text-white" />
                      </div>
                      {action.completed && (
                        <div className="flex items-center space-x-2 bg-[#f5f3ff] text-[#4339A0] px-3 py-1 rounded-full">
                          <CheckCircle className="w-4 h-4" />
                          <span className="text-sm font-medium">{t('promoterDetail.progress.completed')}</span>
                        </div>
                      )}
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-2">{action.title}</h3>
                    <p className="text-gray-600 mb-4">{action.description}</p>

                    <div className="flex items-center justify-between">
                      <div className="text-2xl font-bold text-[#4339A0]">
                        {formatCurrency(action.reward)}
                        {action.perCall && <span className="text-sm text-gray-500"> {t('promoterDetail.progress.perCall')}</span>}
                        {action.perReferral && <span className="text-sm text-gray-500"> {t('promoterDetail.progress.perReferral')}</span>}
                      </div>
                      {action.completed && action.completedDate && (
                        <div className="text-sm text-gray-500">
                          {formatDate(action.completedDate)}
                        </div>
                      )}
                    </div>

                    {action.totalCalls && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="text-sm text-gray-600">{t('promoterDetail.progress.totalCalls')} <span className="font-semibold text-gray-900">{action.totalCalls}</span></div>
                      </div>
                    )}

                    {action.totalReferrals && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="text-sm text-gray-600">{t('promoterDetail.progress.totalReferrals')} <span className="font-semibold text-gray-900">{action.totalReferrals}</span></div>
                      </div>
                    )}

                    {showReminderButton && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <button
                          onClick={handleSendReminder}
                          className="flex items-center space-x-2 px-4 py-2 bg-[#4339A0] text-white rounded-lg hover:bg-[#3d2f8f] transition-colors font-medium"
                        >
                          <Mail className="w-4 h-4" />
                          <span>{t('promoterDetail.progress.sendReminder')}</span>
                        </button>
                      </div>
                    )}

                    {reminderAlreadySent && !action.completed && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="flex items-center space-x-2 text-sm text-gray-500">
                          <Mail className="w-4 h-4" />
                          <span>{t('promoterDetail.progress.reminderSent')}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {activeTab === 'reviews' && (
          <div className="space-y-6">
            {reviews.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12 text-center">
                <Star className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('promoterDetail.reviews.emptyTitle')}</h3>
                <p className="text-gray-600">{t('promoterDetail.reviews.emptyDesc')}</p>
              </div>
            ) : (
              reviews.map((review: {
                id: string;
                rating: number;
                title: string;
                comment: string;
                date: string;
                product: string;
                verified: boolean;
                helpful: number;
                categories: string[];
                publicUrl: string;
                rawTestimonial?: PromoterTestimonialResponse;
                vendorResponse: { author: string; date: string; message: string } | null;
              }) => (
                <div key={review.id} className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <div className="flex items-center space-x-2 mb-2">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`w-6 h-6 ${i < review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
                              }`}
                          />
                        ))}
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">{review.title}</h3>
                      <div className="text-sm text-gray-500">{formatDate(review.date)} • {review.product}</div>
                    </div>
                    {review.verified && (
                      <div className="flex items-center space-x-2 bg-[#f5f3ff] text-[#4339A0] px-4 py-2 rounded-full">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-medium">{t('promoterDetail.reviews.verified')}</span>
                      </div>
                    )}
                  </div>

                  <p className="text-gray-700 text-lg leading-relaxed mb-6">{review.comment}</p>

                  <div className="flex flex-wrap gap-2 mb-6">
                    {(review.categories || []).map((category: string, idx: number) => (
                      <span key={idx} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm">
                        {category}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                    <div className="text-sm text-gray-600">
                      {t('promoterDetail.reviews.helpful', { count: review.helpful })}
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setDetailModalState({ type: 'review', data: review })}
                        className="flex items-center space-x-2 px-4 py-2 bg-[#4339A0] text-white rounded-lg hover:bg-[#3d2f8f] font-medium transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                        <span>{t('promoterDetail.reviews.viewDetails')}</span>
                      </button>
                      {review.publicUrl && (
                        <a
                          href={review.publicUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center space-x-2 text-[#4339A0] hover:text-[#3d2f8f] font-medium"
                        >
                          <span>{t('promoterDetail.reviews.viewPublic')}</span>
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </div>

                  {review.vendorResponse && (() => {
                    const vendorResponse = review.vendorResponse;
                    return (
                      <div className="mt-6">
                        {editingResponse === review.id ? (
                          <div className="bg-gray-50 rounded-xl p-6">
                            <div className="flex items-center justify-between mb-4">
                              <h4 className="font-semibold text-gray-900">{t('promoterDetail.reviews.editResponse')}</h4>
                              <button
                                onClick={() => {
                                  setIsGenerating(true);
                                  setTimeout(() => {
                                    const generatedResponse = `Thank you so much for this wonderful ${review.rating}-star review, ${promoter.name.split(' ')[0]}! We truly appreciate your detailed feedback about ${review.product}. Your insights are invaluable to us and help us continue improving our services. We're thrilled to have you as part of our community!`;
                                    setEditResponseText(generatedResponse);
                                    setIsGenerating(false);
                                  }, 1500);
                                }}
                                disabled={isGenerating}
                                className="flex items-center space-x-2 px-4 py-2 bg-[#4339A0] text-white rounded-lg hover:bg-[#3d2f8f] transition-colors disabled:opacity-50"
                              >
                                <Wand2 className="w-4 h-4" />
                                <span>{isGenerating ? t('promoterDetail.reviews.generatingResponse') : t('promoterDetail.reviews.generateResponse')}</span>
                              </button>
                            </div>
                            <textarea
                              value={editResponseText}
                              onChange={(e) => setEditResponseText(e.target.value)}
                              placeholder={t('promoterDetail.reviews.editPlaceholder')}
                              rows={4}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4339A0] focus:border-transparent resize-none"
                            />
                            <div className="flex justify-end space-x-3 mt-4">
                              <button
                                onClick={() => {
                                  setEditingResponse(null);
                                  setEditResponseText('');
                                }}
                                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                              >
                                {t('promoterDetail.reviews.cancel')}
                              </button>
                              <button
                                onClick={() => {
                                  toast.success(t('promoterDetail.reviews.responseUpdated'));
                                  setEditingResponse(null);
                                  setEditResponseText('');
                                }}
                                className="flex items-center space-x-2 px-6 py-2 bg-[#4339A0] text-white rounded-lg hover:bg-[#3d2f8f] transition-colors"
                              >
                                <Send className="w-4 h-4" />
                                <span>{t('promoterDetail.reviews.saveResponse')}</span>
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="bg-[#f5f3ff] border-l-4 border-[#4339A0] rounded-lg p-6">
                            <div className="flex items-start space-x-4">
                              <div className="w-12 h-12 bg-[#4339A0] rounded-full flex items-center justify-center flex-shrink-0">
                                <MessageSquare className="w-6 h-6 text-white" />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center space-x-2">
                                    <span className="font-bold text-gray-900">{vendorResponse.author}</span>
                                    <span className="text-sm text-gray-500">• {formatDate(vendorResponse.date)}</span>
                                  </div>
                                  <button
                                    onClick={() => {
                                      setEditingResponse(review.id);
                                      setEditResponseText(vendorResponse.message);
                                    }}
                                    className="flex items-center space-x-2 text-[#4339A0] hover:text-[#3d2f8f] font-medium transition-colors"
                                  >
                                    <Edit className="w-4 h-4" />
                                    <span>{t('promoterDetail.reviews.editResponseBtn')}</span>
                                  </button>
                                </div>
                                <p className="text-gray-700 leading-relaxed">{vendorResponse.message}</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {!review.vendorResponse && (
                    <div className="mt-6">
                      {showReviewResponse === review.id ? (
                        <div className="bg-gray-50 rounded-xl p-6">
                          <div className="flex items-center justify-between mb-4">
                            <h4 className="font-semibold text-gray-900">{t('promoterDetail.reviews.writeResponse')}</h4>
                            <button
                              onClick={() => {
                                setIsGenerating(true);
                                setTimeout(() => {
                                  const generatedResponse = `Thank you so much for this wonderful ${review.rating}-star review, ${promoter.name.split(' ')[0]}! We truly appreciate your detailed feedback about ${review.product}. Your insights are invaluable to us and help us continue improving our services. We're thrilled to have you as part of our community!`;
                                  setReviewResponseText(generatedResponse);
                                  setIsGenerating(false);
                                }, 1500);
                              }}
                              disabled={isGenerating}
                              className="flex items-center space-x-2 px-4 py-2 bg-[#4339A0] text-white rounded-lg hover:bg-[#3d2f8f] transition-colors disabled:opacity-50"
                            >
                              <Wand2 className="w-4 h-4" />
                              <span>{isGenerating ? t('promoterDetail.reviews.generatingResponse') : t('promoterDetail.reviews.generateResponse')}</span>
                            </button>
                          </div>
                          <textarea
                            value={reviewResponseText}
                            onChange={(e) => setReviewResponseText(e.target.value)}
                            placeholder={t('promoterDetail.reviews.writePlaceholder')}
                            rows={4}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4339A0] focus:border-transparent resize-none"
                          />
                          <div className="flex justify-end space-x-3 mt-4">
                            <button
                              onClick={() => {
                                setShowReviewResponse(null);
                                setReviewResponseText('');
                              }}
                              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                              {t('promoterDetail.reviews.cancel')}
                            </button>
                            <button
                              onClick={() => {
                                toast.success(t('promoterDetail.reviews.responseSent'));
                                setShowReviewResponse(null);
                                setReviewResponseText('');
                              }}
                              className="flex items-center space-x-2 px-6 py-2 bg-[#4339A0] text-white rounded-lg hover:bg-[#3d2f8f] transition-colors"
                            >
                              <Send className="w-4 h-4" />
                              <span>{t('promoterDetail.reviews.sendResponse')}</span>
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => setShowReviewResponse(review.id)}
                          className="flex items-center space-x-2 px-6 py-3 bg-[#4339A0] text-white rounded-lg hover:bg-[#3d2f8f] font-medium transition-colors"
                        >
                          <MessageSquare className="w-5 h-5" />
                          <span>{t('promoterDetail.reviews.addResponse')}</span>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )))}
          </div>
        )}

        {activeTab === 'testimonials' && (
          <div className="space-y-6">
            {canEditPromoter && (
              <div className="flex items-center justify-end">
                <button
                  onClick={() => setShowUploadVideoModal(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <UploadCloud className="w-4 h-4" />
                  <span>{t('testimonials.uploadVideo.button')}</span>
                </button>
              </div>
            )}
            <div className="grid md:grid-cols-2 gap-6">
            {videoTestimonials.map((video: {
              id: string;
              title: string;
              description: string;
              thumbnailUrl: string;
              videoUrl: string;
              duration: string;
              views: number;
              date: string;
              status: string;
              tags: string[];
              transcript: string;
              likes: number;
              rawTestimonial?: PromoterTestimonialResponse;
              vendorResponse: { author: string; date: string; message: string } | null;
            }) => (
              <div key={video.id} className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
                <div
                  className="relative group cursor-pointer"
                  onClick={() => {
                    setPlayingVideoUrl(video.videoUrl);
                    setPlayingVideoTitle(video.title);
                    setPlayingVideoDescription(video.description);
                    setPlayingVideoTags(video.tags || []);
                  }}
                >
                  {video.thumbnailUrl ? (
                    <img
                      src={video.thumbnailUrl}
                      alt={video.title}
                      className="w-full h-64 object-cover"
                    />
                  ) : video.videoUrl ? (
                    // No stored thumbnail (e.g. vendor-uploaded videos): use the video's
                    // first frame as a poster instead of a broken <img>.
                    <video
                      src={`${video.videoUrl}#t=0.1`}
                      preload="metadata"
                      muted
                      playsInline
                      className="w-full h-64 object-cover bg-gray-900"
                    />
                  ) : (
                    <div className="w-full h-64 bg-gray-200 flex items-center justify-center">
                      <Play className="w-16 h-16 text-gray-400" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/50 transition-colors flex items-center justify-center">
                    <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Play className="w-10 h-10 text-[#4339A0] ml-1" />
                    </div>
                  </div>
                  <div className="absolute top-4 right-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm font-medium">
                    {video.duration}
                  </div>
                </div>

                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-1">{video.title}</h3>
                  {video.description && (
                    <p className="text-gray-600 mb-4 line-clamp-1">{video.description}</p>
                  )}

                  {video.tags && video.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {video.tags.map((tag: string, idx: number) => (
                        <span key={idx} className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                      <div className="flex items-center space-x-1">
                        <Eye className="w-4 h-4" />
                        <span>{video.views.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Star className="w-4 h-4" />
                        <span>{video.likes}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-sm text-gray-500">{formatDate(video.date)}</div>
                      <button
                        onClick={() => setDetailModalState({ type: 'video', data: video })}
                        className="flex items-center space-x-2 px-4 py-2 bg-[#4339A0] text-white rounded-lg hover:bg-[#3d2f8f] font-medium transition-colors text-sm"
                      >
                        <Eye className="w-4 h-4" />
                        <span>{t('promoterDetail.videos.viewDetails')}</span>
                      </button>
                    </div>
                  </div>

                  {video.vendorResponse && (
                    <div className="mt-4 bg-[#f5f3ff] border-l-4 border-[#4339A0] rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <MessageSquare className="w-5 h-5 text-[#4339A0] flex-shrink-0 mt-1" />
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <span className="font-bold text-gray-900 text-sm">{video.vendorResponse.author}</span>
                            <span className="text-xs text-gray-500">• {formatDate(video.vendorResponse.date)}</span>
                          </div>
                          <p className="text-sm text-gray-700 leading-relaxed">{video.vendorResponse.message}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {!video.vendorResponse && (
                    <div className="mt-4">
                      {showVideoResponse === video.id ? (
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h4 className="font-semibold text-gray-900 mb-3 text-sm">{t('promoterDetail.videos.writeResponse')}</h4>
                          <textarea
                            value={videoResponseText}
                            onChange={(e) => setVideoResponseText(e.target.value)}
                            placeholder={t('promoterDetail.videos.writePlaceholder')}
                            rows={3}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4339A0] focus:border-transparent resize-none"
                          />
                          <div className="flex justify-end space-x-2 mt-3">
                            <button
                              onClick={() => {
                                setShowVideoResponse(null);
                                setVideoResponseText('');
                              }}
                              className="px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                              {t('promoterDetail.videos.cancel')}
                            </button>
                            <button
                              onClick={() => {
                                toast.success(t('promoterDetail.videos.responseSent'));
                                setShowVideoResponse(null);
                                setVideoResponseText('');
                              }}
                              className="flex items-center space-x-2 px-4 py-1.5 text-sm bg-[#4339A0] text-white rounded-lg hover:bg-[#3d2f8f] transition-colors"
                            >
                              <Send className="w-4 h-4" />
                              <span>{t('promoterDetail.videos.send')}</span>
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => setShowVideoResponse(video.id)}
                          className="flex items-center space-x-2 text-[#4339A0] hover:text-[#3d2f8f] font-medium text-sm transition-colors"
                        >
                          <MessageSquare className="w-4 h-4" />
                          <span>{t('promoterDetail.videos.respondToVideo')}</span>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
            </div>
          </div>
        )}

        {activeTab === 'usecases' && (
          <div className="space-y-6">
            <p className="text-sm text-gray-600">
              {t('promoterDetail.usecases.count', { count: useCases.length })}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {useCases.map((usecase: {
                id: string;
                title: string;
                description: string;
                fullDescription: string;
                tags: string[];
                status: string;
                views: number;
                downloads: number;
                publishedDate: string;
                industry: string;
                companySize: string;
                challenges: string[];
                solutions: string[];
                results: string[];
                pdfUrl: string;
                featured: boolean;
                rawTestimonial?: PromoterTestimonialResponse;
              }) => (
                <div key={usecase.id} className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden flex flex-col">
                  {usecase.featured && (
                    <div className="bg-gradient-to-r from-yellow-400 to-orange-500 px-4 py-1.5">
                      <div className="flex items-center space-x-2 text-white font-medium text-sm">
                        <Star className="w-4 h-4 fill-white" />
                        <span>{t('promoterDetail.usecases.featured')}</span>
                      </div>
                    </div>
                  )}
                  <div className="p-6 flex-1 flex flex-col">
                    <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2">{usecase.title}</h3>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-3 flex-1">{usecase.description || t('promoterDetail.usecases.noDescription')}</p>
                    <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                      <span className="text-xs text-gray-500">{formatDate(usecase.publishedDate)}</span>
                      <button
                        onClick={() => id && usecase.rawTestimonial && navigate('/use-case-submission/' + usecase.id, { state: { testimonial: { ...usecase.rawTestimonial, promoterId: id, promoterName: promoter.name, promoterCompany: promoter.company } } })}
                        className="flex items-center space-x-2 px-4 py-2 bg-[#4339A0] text-white rounded-lg hover:bg-[#3d2f8f] transition-colors text-sm"
                      >
                        <Eye className="w-4 h-4" />
                        <span>{t('promoterDetail.usecases.viewDetails')}</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'meetings' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {meetingsRequested.length > 0 && (
              <div className="flex flex-col">
                <h2 className="text-lg font-bold text-gray-900 mb-4">{t('promoterDetail.meetings.requested')}</h2>
                <div className="space-y-4">
                  {meetingsRequested.map((meeting: any) => (
                    <div key={meeting.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="text-sm font-bold text-gray-900">{meeting.prospectName}</h3>
                          <p className="text-xs text-gray-600">{meeting.prospectPosition}</p>
                          <p className="text-xs text-gray-500">{meeting.prospectCompany}</p>
                        </div>
                        <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full text-xs font-medium">
                          {t('promoterDetail.meetings.pending')}
                        </span>
                      </div>

                      <div className="space-y-2 mb-3">
                        <div className="text-xs">
                          <span className="text-gray-500">{t('promoterDetail.meetings.topic')}</span>
                          <p className="text-gray-900 font-medium">{meeting.topic}</p>
                        </div>
                        <div className="text-xs">
                          <span className="text-gray-500">{t('promoterDetail.meetings.notes')}</span>
                          <p className="text-gray-700">{meeting.notes}</p>
                        </div>
                      </div>

                      <div className="pt-3 border-t border-gray-200">
                        <div className="text-xs text-gray-500 mb-2">{t('promoterDetail.meetings.preferredDates')}</div>
                        <div className="flex flex-wrap gap-1">
                          {(meeting.preferredDates || []).map((date: any, idx: number) => (
                            <span key={idx} className="bg-[#f5f3ff] text-[#4339A0] px-2 py-1 rounded text-xs">
                              {formatDate(date)}
                            </span>
                          ))}
                        </div>
                        <button
                          onClick={() => openMeetingModal(meeting.id)}
                          className="mt-3 flex items-center space-x-2 px-3 py-1.5 bg-[#4339A0] text-white rounded-lg hover:bg-[#3d2f8f] text-xs font-medium transition-colors"
                        >
                          <Eye className="w-3 h-3" />
                          <span>{t('promoterDetail.meetings.viewDetails')}</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {upcomingMeetings.length > 0 && (
              <div className="flex flex-col">
                <h2 className="text-lg font-bold text-gray-900 mb-4">{t('promoterDetail.meetings.upcoming')}</h2>
                <div className="space-y-4">
                  {upcomingMeetings.map((meeting: any) => (
                    <div key={meeting.id} className="bg-gradient-to-br from-[#f5f3ff] to-[#ebe5ff] rounded-xl shadow-sm border border-[#b8b0f0] p-4">
                      <div className="flex items-start space-x-3 mb-3">
                        <Avatar
                          src={meeting.prospectAvatar}
                          name={meeting.prospectName}
                          size="md"
                        />
                        <div className="flex-1">
                          <h3 className="text-sm font-bold text-gray-900">{meeting.prospectName}</h3>
                          <p className="text-xs text-gray-600">{meeting.prospectPosition}</p>
                          <p className="text-xs text-gray-500">{meeting.prospectCompany}</p>
                        </div>
                      </div>

                      <div className="bg-white rounded-lg p-3 mb-3">
                        <div className="flex items-center space-x-2 mb-1">
                          <Calendar className="w-4 h-4 text-[#4339A0]" />
                          <span className="text-xs font-semibold text-gray-900">{formatDate(meeting.scheduledDate)} at {meeting.scheduledTime}</span>
                        </div>
                        <div className="flex items-center space-x-2 text-xs text-gray-600">
                          <Clock className="w-3 h-3" />
                          <span>{t('promoterDetail.meetings.minutes', { count: meeting.duration })}</span>
                        </div>
                      </div>

                      <div className="mb-3">
                        <div className="text-xs font-semibold text-gray-900 mb-1">{t('promoterDetail.meetings.topic')}</div>
                        <p className="text-xs text-gray-700">{meeting.topic}</p>
                      </div>

                      <div className="flex items-center justify-center space-x-2 bg-gray-100 text-gray-500 px-4 py-2 rounded-lg w-full text-xs">
                        <Video className="w-4 h-4" />
                        <span>{t('promoterDetail.meetings.meetingLink')}</span>
                      </div>
                      <button
                        onClick={() => openMeetingModal(meeting.id)}
                        className="mt-3 w-full flex items-center justify-center space-x-2 px-4 py-2 bg-[#4339A0] text-white rounded-lg hover:bg-[#3d2f8f] text-sm font-medium transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                        <span>{t('promoterDetail.meetings.viewDetails')}</span>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-col">
              <h2 className="text-lg font-bold text-gray-900 mb-4">{t('promoterDetail.meetings.rescheduled')}</h2>
              <div className="space-y-4">
                {rescheduledMeetings.length === 0 ? (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 text-center text-gray-500 text-sm">
                    {t('promoterDetail.meetings.noRescheduled')}
                  </div>
                ) : (
                  rescheduledMeetings.map((meeting: any) => (
                    <div key={meeting.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="text-sm font-bold text-gray-900">{meeting.prospectName}</h3>
                          <p className="text-xs text-gray-500">{meeting.prospectCompany}</p>
                        </div>
                        <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs font-medium">
                          {t('promoterDetail.meetings.rescheduledBadge')}
                        </span>
                      </div>
                      {meeting.notes && (
                        <div className="text-xs text-gray-600 mb-2">{meeting.notes}</div>
                      )}
                      <div className="pt-3 border-t border-gray-200">
                        <div className="text-xs text-gray-500 mb-2">{t('promoterDetail.meetings.proposedDates')}</div>
                        <div className="flex flex-wrap gap-1">
                          {(meeting.rescheduledDates || []).map((date: string, idx: number) => (
                            <span key={idx} className="bg-[#f5f3ff] text-[#4339A0] px-2 py-1 rounded text-xs">
                              {formatDate(date)}
                            </span>
                          ))}
                        </div>
                        <button
                          onClick={() => openMeetingModal(meeting.id)}
                          className="mt-3 flex items-center space-x-2 px-3 py-1.5 bg-[#4339A0] text-white rounded-lg hover:bg-[#3d2f8f] text-xs font-medium transition-colors"
                        >
                          <Eye className="w-3 h-3" />
                          <span>{t('promoterDetail.meetings.viewDetails')}</span>
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {meetingHistory.length > 0 && (
              <div className="flex flex-col">
                <h2 className="text-lg font-bold text-gray-900 mb-4">{t('promoterDetail.meetings.history')}</h2>
                <div className="space-y-4">
                  {meetingHistory.map((meeting: any) => (
                    <div key={meeting.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="text-sm font-bold text-gray-900">{meeting.prospectName}</h3>
                          <p className="text-xs text-gray-600">{meeting.prospectPosition} • {meeting.prospectCompany}</p>
                          <div className="flex items-center space-x-2 mt-1 text-xs text-gray-500">
                            <span>{formatDate(meeting.meetingDate)} at {meeting.meetingTime}</span>
                            <span>•</span>
                            <span>{meeting.duration != null ? t('promoterDetail.meetings.min', { count: meeting.duration }) : t('promoterDetail.meetings.durationNa')}</span>
                          </div>
                        </div>
                        <div className="text-right">
                          {meeting.outcome === 'converted' && (
                            <div className="bg-[#f5f3ff] text-[#4339A0] px-2 py-1 rounded-full text-xs font-medium mb-1">
                              {t('promoterDetail.meetings.converted')}
                            </div>
                          )}
                          {meeting.rewardEarned > 0 && (
                            <div className="text-sm font-bold text-[#4339A0]">
                              +{formatCurrency(meeting.rewardEarned)}
                            </div>
                          )}
                        </div>
                      </div>

                      {meeting.feedback && (
                        <div className="bg-gray-50 rounded-lg p-3 mb-2">
                          <div className="flex items-center space-x-1 mb-1">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`w-3 h-3 ${i < meeting.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
                                  }`}
                              />
                            ))}
                          </div>
                          <p className="text-xs text-gray-700 italic">"{meeting.feedback}"</p>
                        </div>
                      )}

                      {meeting.notes && (
                        <p className="text-xs text-gray-600">{meeting.notes}</p>
                      )}

                      {(meeting.promoterJoinedAt || meeting.prospectJoinedAt || meeting.meetingStartedAt || meeting.meetingEndedAt) && (
                        <div className="mt-2 text-xs text-gray-500">
                          {meeting.promoterJoinedAt && (
                            <div>{t('promoterDetail.meetings.promoterJoined', { time: formatDateTime(meeting.promoterJoinedAt) })}</div>
                          )}
                          {meeting.prospectJoinedAt && (
                            <div>{t('promoterDetail.meetings.prospectJoined', { time: formatDateTime(meeting.prospectJoinedAt) })}</div>
                          )}
                          {meeting.meetingStartedAt && (
                            <div>{t('promoterDetail.meetings.started', { time: formatDateTime(meeting.meetingStartedAt) })}</div>
                          )}
                          {meeting.meetingEndedAt && (
                            <div>{t('promoterDetail.meetings.ended', { time: formatDateTime(meeting.meetingEndedAt) })}</div>
                          )}
                        </div>
                      )}
                      <button
                        onClick={() => openMeetingModal(meeting.id)}
                        className="mt-3 flex items-center space-x-2 px-3 py-1.5 bg-[#4339A0] text-white rounded-lg hover:bg-[#3d2f8f] text-xs font-medium transition-colors"
                      >
                        <Eye className="w-3 h-3" />
                        <span>{t('promoterDetail.meetings.viewDetails')}</span>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'rewards' && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">{t('promoterDetail.rewards.type')}</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">{t('promoterDetail.rewards.description')}</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">{t('promoterDetail.rewards.dateEarned')}</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">{t('promoterDetail.rewards.status')}</th>
                    <th className="px-6 py-4 text-right text-sm font-semibold text-gray-900">{t('promoterDetail.rewards.amount')}</th>
                    <th className="px-6 py-4 text-right text-sm font-semibold text-gray-900">{t('promoterDetail.rewards.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {rewardHistory.map((reward: any) => {
                    const typeLabels: Record<string, { label: string; color: string; icon: any }> = {
                      REFERRAL: { label: t('promoterDetail.rewards.referral'), color: 'bg-[#f5f3ff] text-[#4339A0]', icon: Users },
                      REFERENCE_CALL: { label: t('promoterDetail.rewards.referenceCall'), color: 'bg-[#ebe5ff] text-[#5a4dba]', icon: Phone },
                      VIDEO: { label: t('promoterDetail.rewards.videoTestimonial'), color: 'bg-red-100 text-red-700', icon: Video },
                      USE_CASE: { label: t('promoterDetail.rewards.useCase'), color: 'bg-purple-100 text-purple-700', icon: FileText },
                      REVIEW: { label: t('promoterDetail.rewards.review'), color: 'bg-yellow-100 text-yellow-700', icon: Star },
                      BONUS: { label: t('promoterDetail.rewards.bonusReward'), color: 'bg-green-100 text-green-700', icon: Gift },
                      // Handle lowercase variants
                      referral: { label: t('promoterDetail.rewards.referral'), color: 'bg-[#f5f3ff] text-[#4339A0]', icon: Users },
                      reference_call: { label: t('promoterDetail.rewards.referenceCall'), color: 'bg-[#ebe5ff] text-[#5a4dba]', icon: Phone },
                      video: { label: t('promoterDetail.rewards.videoTestimonial'), color: 'bg-red-100 text-red-700', icon: Video },
                      use_case: { label: t('promoterDetail.rewards.useCase'), color: 'bg-purple-100 text-purple-700', icon: FileText },
                      review: { label: t('promoterDetail.rewards.review'), color: 'bg-yellow-100 text-yellow-700', icon: Star },
                      bonus: { label: t('promoterDetail.rewards.bonusReward'), color: 'bg-green-100 text-green-700', icon: Gift }
                    };

                    const rewardType = (reward.activityType || reward.type || '').toUpperCase();
                    const typeInfo = typeLabels[rewardType] || typeLabels[reward.activityType] || typeLabels[reward.type] || { label: reward.activityType || reward.type || t('promoterDetail.rewards.unknown'), color: 'bg-gray-100 text-gray-700', icon: Award };
                    const Icon = typeInfo.icon || Award;

                    // Static description per type - no full content
                    const rewardDescription = typeInfo.label;
                    const testimonialId = reward.testimonialId;
                    const hasViewLink = testimonialId && id && ['REVIEW', 'VIDEO', 'USE_CASE'].includes(rewardType);

                    return (
                      <tr key={reward.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-3">
                            <div className={`w-10 h-10 rounded-lg ${typeInfo?.color} flex items-center justify-center flex-shrink-0`}>
                              <Icon className="w-5 h-5" />
                            </div>
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${typeInfo?.color} whitespace-nowrap`}>
                              {typeInfo?.label}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">
                            {rewardDescription}
                          </div>
                          {reward.prospectName && (rewardType === 'REFERENCE_CALL' || rewardType === 'REFERRAL') && (
                            <div className="text-xs text-gray-600 mt-1">{reward.prospectName} • {reward.prospectCompany}</div>
                          )}
                          {reward.bonusMessage && (
                            <div className="text-xs text-green-600 mt-1 italic">{reward.bonusMessage}</div>
                          )}
                          {hasViewLink && viewDetailsPath && (
                            <button
                              onClick={() => openDetailModalByTestimonialId(testimonialId, rewardType)}
                              className="mt-2 flex items-center space-x-1 text-[#4339A0] hover:text-[#3d2f8f] font-medium text-sm"
                            >
                              <Eye className="w-4 h-4" />
                              <span>View Details</span>
                            </button>
                          )}
                          {rewardType === 'REFERENCE_CALL' && id && (
                            <button
                              onClick={() => { setActiveTab('meetings'); }}
                              className="mt-2 flex items-center space-x-1 text-[#4339A0] hover:text-[#3d2f8f] font-medium text-sm"
                            >
                              <Eye className="w-4 h-4" />
                              <span>View in Meetings</span>
                            </button>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600 whitespace-nowrap">
                          {reward.createdAt ? formatDate(reward.createdAt) : reward.date ? formatDate(reward.date) : '-'}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${reward.status === 'PAID' || reward.status === 'paid' ? 'bg-purple-100 text-purple-700' :
                              reward.status === 'COMPLETED' || reward.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                                reward.status === 'PENDING' || reward.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-red-100 text-red-700'
                            }`}>
                            {reward.status ? reward.status.charAt(0).toUpperCase() + reward.status.slice(1).toLowerCase() : 'Unknown'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="text-lg font-bold text-[#4339A0] whitespace-nowrap">
                            +{formatCurrency(reward.amount)}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right">
                          {(reward.status === 'PENDING' || reward.status === 'pending') && reward.id && (
                            <button
                              onClick={async () => {
                                try {
                                  await payPendingReward(reward.id).unwrap();
                                  toast.success(`Reward of ${formatCurrency(reward.amount)} paid successfully`);
                                } catch (err: any) {
                                  toast.error(err?.data?.message || err?.message || 'Failed to pay reward');
                                }
                              }}
                              disabled={isPayingReward}
                              className="flex items-center space-x-1 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium ml-auto disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <CreditCard className="w-4 h-4" />
                              <span>Pay</span>
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <VideoModal
        isOpen={!!playingVideoUrl}
        onClose={() => {
          setPlayingVideoUrl(null);
          setPlayingVideoTitle(undefined);
          setPlayingVideoDescription(undefined);
          setPlayingVideoTags([]);
        }}
        videoUrl={playingVideoUrl || ''}
        title={playingVideoTitle}
        description={playingVideoDescription}
        tags={playingVideoTags}
      />

      <MeetingDetailsModal
        meeting={selectedMeetingForModal}
        isOpen={isMeetingModalOpen}
        onClose={() => {
          setIsMeetingModalOpen(false);
          setSelectedMeetingForModal(null);
        }}
      />

      {detailModalState && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setDetailModalState(null)} />
            <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
                <h2 className="text-xl font-bold text-gray-900">
                  {detailModalState.type === 'review' && 'Review Details'}
                  {detailModalState.type === 'video' && 'Video Details'}
                  {detailModalState.type === 'usecase' && 'Use Case Details'}
                </h2>
                <button onClick={() => setDetailModalState(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>
              <div className="p-6">
                {detailModalState.type === 'review' && detailModalState.data && (
                  <div className="space-y-4">
                    <div className="flex items-center space-x-2">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className={`w-6 h-6 ${i < detailModalState.data.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
                      ))}
                      <span className="text-sm text-gray-500">{formatDate(detailModalState.data.date)}</span>
                    </div>
                    <p className="text-gray-700 leading-relaxed">{detailModalState.data.comment}</p>
                    {detailModalState.data.vendorResponse && (
                      <div className="bg-[#f5f3ff] border-l-4 border-[#4339A0] p-4 rounded">
                        <p className="text-sm font-medium text-gray-600 mb-1">Vendor Response</p>
                        <p className="text-gray-700">{detailModalState.data.vendorResponse.message}</p>
                      </div>
                    )}
                  </div>
                )}
                {detailModalState.type === 'video' && detailModalState.data && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">{detailModalState.data.title}</h3>
                    {detailModalState.data.description && <p className="text-gray-600">{detailModalState.data.description}</p>}
                    <p className="text-sm text-gray-500">{formatDate(detailModalState.data.date)}</p>
                    {detailModalState.data.videoUrl && (
                      <button
                        onClick={() => {
                          setPlayingVideoUrl(detailModalState.data.videoUrl);
                          setPlayingVideoTitle(detailModalState.data.title);
                          setPlayingVideoDescription(detailModalState.data.description);
                          setPlayingVideoTags(detailModalState.data.tags || []);
                          setDetailModalState(null);
                        }}
                        className="flex items-center space-x-2 px-4 py-2 bg-[#4339A0] text-white rounded-lg hover:bg-[#3d2f8f]"
                      >
                        <Play className="w-5 h-5" />
                        <span>Play Video</span>
                      </button>
                    )}
                    {detailModalState.data.vendorResponse && (
                      <div className="bg-[#f5f3ff] border-l-4 border-[#4339A0] p-4 rounded">
                        <p className="text-sm font-medium text-gray-600 mb-1">Vendor Response</p>
                        <p className="text-gray-700">{detailModalState.data.vendorResponse.message}</p>
                      </div>
                    )}
                  </div>
                )}
                {detailModalState.type === 'usecase' && detailModalState.data && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">{detailModalState.data.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{detailModalState.data.description}</p>
                    <p className="text-sm text-gray-500">{formatDate(detailModalState.data.publishedDate)}</p>
                    <button
                      onClick={() => id && detailModalState.data.rawTestimonial && navigate('/use-case-submission/' + detailModalState.data.id, { state: { testimonial: { ...detailModalState.data.rawTestimonial, promoterId: id } } })}
                      className="flex items-center space-x-2 px-4 py-2 bg-[#4339A0] text-white rounded-lg hover:bg-[#3d2f8f]"
                    >
                      <Eye className="w-4 h-4" />
                      <span>View Full Use Case</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={async () => {
          if (!id) return;
          try {
            await deletePromoter(id).unwrap();
            toast.success('Promoter deleted successfully');
            setShowDeleteDialog(false);
            navigate('/promoters');
          } catch (err: any) {
            toast.error(err?.data?.message || 'Failed to delete promoter');
          }
        }}
        title="Delete Promoter"
        description={`Permanently delete ${promoter.name}? This will remove their account from Firebase and our database. This action cannot be undone.`}
        confirmLabel="Delete Permanently"
        variant="danger"
        isLoading={isDeletingPromoter}
      />

      {promoterResponse && (
        <UploadPromoterVideoModal
          isOpen={showUploadVideoModal}
          onClose={() => setShowUploadVideoModal(false)}
          promoterId={promoterResponse.id}
          promoterName={`${promoterResponse.firstName} ${promoterResponse.lastName}`}
        />
      )}

      {showEditModal && promoterResponse && (
        <EditPromoterModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
            refetch();
          }}
          promoter={{
            id: promoterResponse.id,
            email: promoterResponse.email,
            firstName: promoterResponse.firstName,
            lastName: promoterResponse.lastName,
            phone: promoterResponse.phone,
            company: promoterResponse.company,
            linkedin: promoterResponse.linkedin,
            companyUrl: promoterResponse.companyUrl ?? undefined,
            avatar: promoterResponse.avatar ?? undefined,
            companyLogo: promoterResponse.companyLogo ?? undefined,
            companySize: promoterResponse.companySize,
            industrySectors: promoterResponse.industrySectors,
            timezone: promoterResponse.timezone,
            referenceCallFrequency: promoterResponse.referenceCallFrequency,
          }}
        />
      )}
    </div>
  );
};

export default PromoterDetail;
